import { fetchJson } from "./api";

let cachedSession: any | null = null;
let lastRefreshAt = Date.now();
let sessionTtlMs = 30 * 60 * 1000;
let isLoggingOut = false;

// ========= Storage Keys =========
const KEY_LOGGED_IN = "auth.loggedIn";
const KEY_USERNAME = "auth.username";
const KEY_SESSION = "auth.session";
const KEY_LAST_ROUTE = "auth.lastRoute";
const KEY_AUTHTYPE = "auth.userType";

// ========= Public API =========
export function isLoggedIn(): boolean {
  return sessionStorage.getItem(KEY_LOGGED_IN) === "1";
}

export function getSession() {
  if (cachedSession) return cachedSession;

  const stored = sessionStorage.getItem("auth.session");
  if(!stored) return null;

  try {
    cachedSession = JSON.parse(stored);
    return cachedSession;
  } catch {
    return null;
  }
}

export function clearAuth() {
  cachedSession = null;
  sessionStorage.removeItem(KEY_LOGGED_IN);
  sessionStorage.removeItem(KEY_USERNAME);
  sessionStorage.removeItem(KEY_SESSION);
  emitAuthChanged();
}

export function setAuthFromSession(s: any) {
  if(isLoggingOut) return;

  cachedSession = s;

  sessionStorage.setItem(KEY_LOGGED_IN, "1");

  const display =
    s?.vorname && s?.nachname
      ? `${s.vorname} ${s.nachname}`
      : s?.benutzername || "";

  sessionStorage.setItem(KEY_USERNAME, display);
  sessionStorage.setItem(KEY_SESSION, JSON.stringify(s));
  sessionStorage.setItem(KEY_AUTHTYPE,String(s.benutzerTyp ?? 0));
  lastRefreshAt = Date.now();

  emitAuthChanged();
}

export async function loadSession() {
  try {
    const res = await fetchJson(`/auth/SessionInfo`);

    if (!res?.success || !res?.data) {
      return;
    }

    if (res.data.authenticated !== true) {
      return;
    }

    setAuthFromSession(res.data);
    emitAuthChanged();
  } catch {
  }
}

export type BenutzerDto = {
  benutzerID?: number;
  firma?: string;
  email?: string;
  nachname?: string;
  vorname?: string;
  benutzername?: string;
  benutzerTyp?: number;
  stammBenutzer?: number;
  monteurID?: number;
};

export type SessionDto = {
  benutzername?: string;
  nachname?: string;
  vorname?: string;
  authenticated?: boolean;
  benutzerId?: number;
  benutzerTyp?: number;
  message?: string | null;
  messageAvailable?: boolean;
  benutzer?: BenutzerDto | null;
};

export type LoginResponse = {
  success: boolean;
  session?: SessionDto | null;
  message?: string;
};

// ========= Endpoints =========
const LOGIN_URL = `/auth/login`;
const SESSION_REFRESH_URL = `/auth/SessionInfo`;

// ========= Events =========
function emitAuthChanged() {
  window.dispatchEvent(new CustomEvent("auth-changed"));
}
function emitOnlineChanged() {
  window.dispatchEvent(new CustomEvent("online-changed"));
}

export function getDisplayName(): string {
  return sessionStorage.getItem(KEY_USERNAME) || "";
}

export function getLastRefreshAt() {
  return lastRefreshAt;
}

export async function login(username: string, password: string) {
  const res = await fetchJson(LOGIN_URL, {
    method: "POST",
    credentials: "include",
    redirect: "manual",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      username,
      password
    }),
  });

  if (!res?.success) {
    throw new Error(res?.message || "Login fehlgeschlagen");
  }

  isLoggingOut = false;
  setAuthFromSession(res.data);
}

export async function logout() {
  isLoggingOut = true;

  stopSilentSessionRefresh();
  try{
    await fetchJson(`/auth/logout`, {
      method: "POST"
    });
  }catch{

  }
  clearAuth();
  window.location.href = "/login";
}

export async function refreshSession(): Promise<boolean> {
  if(isLoggingOut) return false;

  try {
    const res = await fetchJson(`/auth/SessionInfo`, {
      method: "GET",
      cache: "no-store"
    });

    if (res?.success && res?.data?.authenticated) {
      setAuthFromSession(res.data);
      return true;
    }

    clearAuth();
    return false;
  } catch {
    clearAuth();
    return false;
  }
}

// ========= Silent Session Refresh =========
let refreshTimer: number | null = null;
let lastRefreshOk = true;

export function getLastRefreshOk() {
  return lastRefreshOk;
}

export async function refreshSessionSilently(): Promise<boolean> {
  if(isLoggingOut) return false;

  if (!isLoggedIn()) {
    lastRefreshOk = false;
    emitOnlineChanged();
    return false;
  }

  try {
    const res = await fetchJson(SESSION_REFRESH_URL, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    });

    if(!res?.success || !res?.data?.authenticated){
      clearAuth();
      return false;
    }

    if (res?.success && res?.data?.authenticated === true) {
      setAuthFromSession(res.data);
      lastRefreshAt = Date.now();
      lastRefreshOk = true;
      emitOnlineChanged();
      return true;
    }

    clearAuth();
    lastRefreshOk = true;
    emitOnlineChanged();
    return false;
  } catch (err: any) {
    if(err.status === 401){
      clearAuth();
      lastRefreshOk = true;
      emitOnlineChanged();
      return true;
    }

    lastRefreshOk = true;
    emitOnlineChanged();
    return true;
  }
}

export function startSilentRefresh(intervalMs = 60_000) {
  stopSilentRefresh();

  refreshTimer = window.setInterval(async () => {
    if (!isLoggedIn()) return;

    const ok = await refreshSession();
    if (!ok) {
      clearAuth();
    }
  }, intervalMs);
}

export function stopSilentRefresh() {
  if (refreshTimer !== null) {
    window.clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

export function setSessionTtlMinutes(minutes: number) {
  const m = Number(minutes);
  if (Number.isFinite(m) && m > 1) {
    sessionTtlMs = m * 60 * 1000;
  }
}

export function getSessionExpiresInMs() {
  if (!isLoggedIn()) return 0;
  const remaining = (lastRefreshAt + sessionTtlMs) - Date.now();
  return Math.max(0, remaining);
}

export function setLastRoute(path: string) {
  if (!path) return;
  sessionStorage.setItem(KEY_LAST_ROUTE, path);
}

export function getLastRoute(): string {
  return sessionStorage.getItem(KEY_LAST_ROUTE) || "";
}

export function clearLastRoute() {
  sessionStorage.removeItem(KEY_LAST_ROUTE);
}

export function startSilentSessionRefresh(opts?: { intervalMs?: number; immediate?: boolean }) {
  const intervalMs = opts?.intervalMs ?? 60_000;
  const immediate = opts?.immediate ?? true;

  stopSilentSessionRefresh();

  window.addEventListener("online", emitOnlineChanged);
  window.addEventListener("offline", emitOnlineChanged);

  if (immediate) void refreshSessionSilently();

  refreshTimer = window.setInterval(() => {
    void refreshSessionSilently();
  }, intervalMs);
}

export function stopSilentSessionRefresh() {
  if (refreshTimer !== null) {
    window.clearInterval(refreshTimer);
    refreshTimer = null;
  }
  window.removeEventListener("online", emitOnlineChanged);
  window.removeEventListener("offline", emitOnlineChanged);
}

export function updateSessionPartial(patch: Partial<SessionDto> & { Benutzer?: Partial<BenutzerDto> }) {
  const current = getSession() || {};
  const merged: any = {
    ...current,
    ...patch,
    benutzer: {
      ...(current as any).benutzer,
      ...(patch as any).benutzer,
    },
  };

  sessionStorage.setItem("auth.session", JSON.stringify(merged));

  const display =
    merged?.Vorname && merged?.Nachname
      ? `${merged.Vorname} ${merged.Nachname}`
      : merged?.Benutzername || merged?.Benutzer?.Benutzername || getDisplayName();

  if (display) sessionStorage.setItem("auth.username", display);

  window.dispatchEvent(new CustomEvent("auth-changed"));
}

export function getBenutzerTyp(): number | null {
  const userType = Number(sessionStorage.getItem("auth.userType"));
  return userType ?? null;
}

export function hasRole(...allowed: number[]): boolean {
  const typ = getBenutzerTyp();
  return typ !== null && allowed.includes(typ);
}

export function getSessionHealth() {
  if (!isLoggedIn()) return "logged-out";
  if (!navigator.onLine) return "offline";
  if (!lastRefreshOk) return "warning";

  const remaining = getSessionExpiresInMs();

  if (remaining <= 0) return "expired";
  if (remaining < 5 * 60 * 1000) return "expiring"; // < 5 Minuten

  return "ok";
}